import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from './StatusBadge';
import CountdownBadge from './CountdownBadge';
import { hapticLight } from '../../native/haptics';

const MobileEventCard = ({ event, onSaveToggle, isSaved }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(dateStr);
  };

  const startDate = parseLocalDate(event.start);
  const endDate = parseLocalDate(event.end);

  const formatDateRange = () => {
    if (!startDate) return '';
    const opts = { month: 'short', day: 'numeric' };
    if (endDate && endDate.toDateString() !== startDate.toDateString()) {
      return `${startDate.toLocaleDateString('en-US', opts)} - ${endDate.toLocaleDateString('en-US', opts)}`;
    }
    return startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const imageUrl = event.image_url || event.image;

  return (
    <Link
      to={`/events/${event.id}`}
      className="block bg-white dark:bg-gray-800 rounded-2xl shadow-card overflow-hidden active:scale-[0.98] transition-transform duration-150"
    >
      {/* Image */}
      <div className="relative w-full h-40 bg-gray-200 dark:bg-gray-700 overflow-hidden">
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
        )}
        {imageError ? (
          <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-primary-light/20 to-primary/10">
            <svg className="w-12 h-12 text-primary/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={event.event || event.title || 'Event'}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        )}
        {/* Save button */}
        {onSaveToggle && (
          <button
            onClick={(e) => {
              e.preventDefault();
              hapticLight();
              onSaveToggle(event.id);
            }}
            className="absolute top-3 right-3 flex items-center justify-center w-9 h-9 rounded-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-900 transition-colors"
            aria-label={isSaved ? 'Remove from saved' : 'Save event'}
          >
            <svg
              className={`w-5 h-5 ${isSaved ? 'text-amber-400 fill-current' : 'text-gray-400'}`}
              fill={isSaved ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
        )}
        {/* Status badge bottom-left */}
        <div className="absolute bottom-3 left-3">
          <StatusBadge status={event.status} size="sm" />
        </div>
      </div>
      {/* Content */}
      <div className="p-4">
        <h3 className="font-display text-base font-bold text-gray-900 dark:text-white leading-tight mb-1 line-clamp-2">
          {event.event || event.title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {event.organization}
        </p>
        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 mb-2">
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="truncate">{formatDateRange()}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="truncate">{event.city}, {event.state || ''}</span>
        </div>
        {/* Countdown */}
        {startDate && event.status !== 'past' && event.status !== 'cancelled' && (
          <div className="mt-3">
            <CountdownBadge targetDate={event.start} size="sm" />
          </div>
        )}
      </div>
    </Link>
  );
};

export default MobileEventCard;
